/**
 * La fiche d'un enfant, telle que son parent la consulte et la corrige.
 *
 * ## Ce que cette page corrige (23/10/2026)
 *
 * Elle avait ses propres champs — sept sections, découpées comme les tables
 * de la base, avec leur propre validation — à côté du formulaire de création
 * et de vérification. Les deux avaient divergé, et la modification par le
 * parent s'était cassée de plusieurs façons :
 *
 * 1. **La fiche lue n'était pas celle qu'on venait de charger.** La page
 *    affichait l'enfant de la LISTE (`kids`) mais ne rechargeait que la fiche
 *    seule (`fetchKid`, qui ne touche pas la liste). Après « Enregistrer » dans
 *    Santé, Activités, Départs ou Inclusion, la page gardait l'ancienne
 *    version : le compteur des manques ne bougeait pas, et rouvrir la section
 *    réaffichait — puis réenregistrait — les anciennes valeurs. Ouverte
 *    directement (lien, rechargement), sans la liste en mémoire, elle restait
 *    sur l'écran de chargement.
 * 2. **Une modification en cours s'effaçait** dès qu'une donnée arrivait en
 *    retard (la classe de l'enfant, une photo) : un effet réinitialisait tous
 *    les champs.
 * 3. **Le registre national bloquait tout.** Depuis le 22/10, il est confronté
 *    à la date de naissance ; un numéro ancien qui ne passait plus empêchait
 *    d'enregistrer la moindre correction d'adresse, sans moyen de le retirer.
 * 4. **Les écrans n'étaient pas ceux que le parent connaît** : numéro national
 *    en texte libre, heure de départ en texte libre, personnes autorisées
 *    ajoutées sur une autre page, droit à l'image dans un composant à part.
 *
 * ## Ce qu'elle est maintenant
 *
 * Le récapitulatif de la création, en mode consultation : cinq blocs, ceux du
 * formulaire. « Modifier » ouvre l'étape à la place de son bloc — les écrans
 * mêmes de la création, avec leurs règles, leurs messages, « Continuer sans ce
 * numéro », la classe, l'ajout d'une personne autorisée sur place — puis
 * « Enregistrer » n'écrit que ce qui a changé (`ecrituresDeLEtape`) et
 * recharge la fiche. Une seule partie ouverte à la fois.
 *
 * Corriger une partie n'est pas vérifier toute la fiche : seule la
 * vérification complète (`/kids/:id/verifier`) la déclare à jour.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Camera, Loader2, User } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { db } from '../../lib/payments/db';
import { useAuthStore } from '../../stores/authStore';
import { useKidStore, type KidWithDetails } from '../../stores/kidStore';
import { useSchoolStore } from '../../stores/schoolStore';
import { useAuthorizedPersonsStore } from '../../stores/authorizedPersonsStore';
import { VERSION_POLITIQUE } from '../../lib/confidentialite';
import { completudeEnfant } from '../../lib/completude';
import { getAgeFromDate } from '../../utils/date';
import { anneeScolaireCourante, resumeClasse, type ClassesConnues } from '../../lib/classes/regles';
import {
  classeDeLEnfant,
  enregistrerClasse,
  lireClassesConnues,
  signalerClasseManquante,
} from '../../lib/classes/gateway.supabase';

import EtapeIdentite from '../../components/forms/kid/EtapeIdentite';
import EtapeSante from '../../components/forms/kid/EtapeSante';
import EtapeVie from '../../components/forms/kid/EtapeVie';
import EtapeSorties from '../../components/forms/kid/EtapeSorties';
import EtapeAutorisations from '../../components/forms/kid/EtapeAutorisations';
import Recapitulatif from '../../components/forms/kid/Recapitulatif';
import LigneClasse from '../../components/forms/kid/LigneClasse';
import { EnModification, TonDesManques } from '../../components/forms/kid/champs';
import { BandeauManques, BarreVerifier } from '../../components/forms/kid/Completude';
import { manquesParEtape, type EtapeFiche } from '../../components/forms/kid/sections';
import {
  depuisLignes,
  ecoleEnregistree,
  ecrituresDeLEtape,
  erreursEtape,
  type ContexteFiche,
  type Erreurs,
  type Fiche,
  type LignesLues,
} from '../../components/forms/kid/fiche';

/** Les lignes de la base, telles que le magasin les rapporte. */
const lignesDe = (k: KidWithDetails): LignesLues => ({
  kids: k,
  health: k.health,
  allergies: k.allergies,
  activities: k.activities,
  departure: k.departure,
  inclusion: k.inclusion,
  image_rights: k.image_rights,
});

/** Le nom d'une table dans un message d'échec : « kid_health » n'apprend rien à une famille. */
const TABLE_LISIBLE: Record<string, string> = {
  kid_health: 'la santé',
  kid_allergies: 'les allergies et le régime',
  kid_activities: 'les activités',
  kid_inclusion: 'les besoins particuliers',
  kid_departure: 'les départs',
  kid_image_rights: 'les autorisations d’image',
};

const KidDetailsPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { user, profile } = useAuthStore();
  const { kids, fetchKids, fetchKid } = useKidStore();
  const { schools, fetchSchools } = useSchoolStore();
  const { persons, fetchPersons } = useAuthorizedPersonsStore();

  /* La fiche telle qu'en base (pour la complétude et la photo), et telle que le formulaire la lit. */
  const [ligne, setLigne] = useState<KidWithDetails | null>(null);
  const [depart, setDepart] = useState<Fiche | null>(null);
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [introuvable, setIntrouvable] = useState(false);

  const [enModification, setEnModificationEtape] = useState<EtapeFiche | null>(null);
  const [erreurs, setErreurs] = useState<Erreurs>({});
  /* Ouverte depuis un manque, l'étape montre ce qui manque en orange ; après un essai d'enregistrement, en rouge. */
  const [tonDesManques, setTonDesManques] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  /* Le garde-fou du double envoi : une référence, pas un état. */
  const enCours = useRef(false);

  const [classesConnues, setClassesConnues] = useState<ClassesConnues | undefined>(undefined);
  const annee = useMemo(() => anneeScolaireCourante(), []);

  /*
    Charger, c'est relire la fiche seule ET sa classe de l'année, puis
    reconstruire le formulaire. Rien ne dépend de la liste des enfants : la
    page s'ouvre aussi bien depuis un lien que depuis la liste.
  */
  const charger = useCallback(async () => {
    const [k, classe] = await Promise.all([fetchKid(id), classeDeLEnfant(id, annee).catch(() => null)]);
    if (!k) {
      setIntrouvable(true);
      return;
    }
    const lue = depuisLignes(lignesDe(k));
    lue.identite.classeId = classe?.id ?? '';
    setLigne(k);
    setDepart(lue);
    setFiche(lue);
  }, [id, fetchKid, annee]);

  useEffect(() => {
    void charger();
    fetchKids();
    fetchSchools();
    fetchPersons();
    let annule = false;
    lireClassesConnues()
      .then((c) => {
        if (!annule) setClassesConnues(c);
      })
      .catch((e) => console.error('Classes des centres illisibles :', e));
    return () => {
      annule = true;
    };
  }, [charger, fetchKids, fetchSchools, fetchPersons]);

  /* Le numéro de registre national du parent et des autres enfants : la fiche ne peut reprendre aucun d'eux. */
  const contexte = useMemo<ContexteFiche>(
    () => ({
      nrnInterdits: [profile?.nnational ?? '', ...kids.filter((k) => k.id !== id).map((k) => k.n_national ?? '')].filter(Boolean),
      classes: classesConnues,
    }),
    [profile, kids, id, classesConnues],
  );

  const completude = useMemo(() => (ligne ? completudeEnfant(lignesDe(ligne)) : null), [ligne]);

  /* --- Ouvrir, annuler, enregistrer une partie --------------------------- */

  const amenerA = (etape: EtapeFiche) =>
    /* Le délai laisse React ouvrir le bloc : sans lui, on défile vers sa taille fermée. */
    setTimeout(() => document.getElementById(`etape-${etape}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);

  const ouvrir = (etape: EtapeFiche) => {
    if (!depart || enModification) return;
    const manques = erreursEtape(etape, depart, contexte);
    setFiche(depart);
    setErreurs(manques);
    setTonDesManques(Object.keys(manques).length > 0);
    setEchec(null);
    setPhoto(null);
    setEnModificationEtape(etape);
    amenerA(etape);
  };

  const annuler = () => {
    const etape = enModification;
    setFiche(depart);
    setErreurs({});
    setEchec(null);
    setPhoto(null);
    setEnModificationEtape(null);
    if (etape) amenerA(etape);
  };

  const enregistrer = async (etape: EtapeFiche) => {
    if (!fiche || !depart || !user || enCours.current) return;

    const trouvees = erreursEtape(etape, fiche, contexte);
    if (Object.keys(trouvees).length > 0) {
      setErreurs(trouvees);
      setTonDesManques(false);
      setEchec(
        Object.keys(trouvees).length === 1
          ? 'Une information est à corriger, signalée ci-dessous.'
          : `${Object.keys(trouvees).length} informations sont à corriger, signalées ci-dessous.`,
      );
      amenerA(etape);
      return;
    }

    enCours.current = true;
    setEnregistrement(true);
    setEchec(null);

    try {
      const ecritures = ecrituresDeLEtape(etape, fiche, depart, id, user.id, VERSION_POLITIQUE);
      let rien = !ecritures.kids && ecritures.lignes.length === 0;

      if (ecritures.kids) {
        const { error } = await db.from('kids').update(ecritures.kids).eq('id', id);
        if (error) throw new Error(`l’identité n’a pas pu être enregistrée (${error.message})`);
      }
      for (const { table, ligne: aEcrire } of ecritures.lignes) {
        const { error } = await db.from(table).upsert(aEcrire, { onConflict: 'kid_id' });
        if (error) throw new Error(`${TABLE_LISIBLE[table] ?? table} n’ont pas pu être enregistrées (${error.message})`);
      }

      if (etape === 'identite') {
        /*
          La classe de l'année, quand l'école ou la classe a changé. Seulement si
          les classes ont pu être lues : sans elles, rien n'a été demandé, et
          rien ne doit être effacé.
        */
        const ecoleChange = ecoleEnregistree(fiche.identite) !== ecoleEnregistree(depart.identite);
        const classeChange = fiche.identite.classeId !== depart.identite.classeId;
        if (classesConnues && (ecoleChange || classeChange)) {
          const ecole = ecoleEnregistree(fiche.identite);
          await enregistrerClasse(id, ecole, ecole, annee, fiche.identite.classeId);
          rien = false;
        }

        /* La photo en dernier, et son échec n'annule rien. */
        if (photo) {
          rien = false;
          const chemin = `${id}.${photo.type === 'image/png' ? 'png' : 'jpg'}`;
          const { error: televersement } = await supabase.storage
            .from('kid-photos')
            .upload(chemin, photo, { upsert: true, contentType: photo.type });
          if (televersement) {
            toast.error('La photo n’a pas pu être envoyée ; le reste est enregistré.');
          } else {
            await db.from('kids').update({ photo_url: chemin }).eq('id', id);
          }
        }
      }

      await charger();
      fetchKids();
      setEnModificationEtape(null);
      setErreurs({});
      setPhoto(null);
      amenerA(etape);
      if (rien) toast('Rien n’a changé.');
      else toast.success('Modifications enregistrées.');
    } catch (err) {
      setEchec(
        err instanceof Error
          ? `L’enregistrement a échoué : ${err.message}. Vos réponses sont conservées, réessayez dans un instant.`
          : 'L’enregistrement a échoué. Vos réponses sont conservées, réessayez dans un instant.',
      );
      amenerA(etape);
    } finally {
      enCours.current = false;
      setEnregistrement(false);
    }
  };

  /* --- Rendu ------------------------------------------------------------- */

  if (introuvable) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="text-gray-700">Cette fiche est introuvable, ou elle n’est pas rattachée à votre compte.</p>
        <Link to="/kids" className="btn-primary mt-6 inline-flex">
          Revenir à mes enfants
        </Link>
      </div>
    );
  }

  if (!ligne || !fiche || !depart || !completude) {
    return (
      <div className="flex justify-center py-24" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" aria-label="Chargement de la fiche" />
      </div>
    );
  }

  const prenom = depart.identite.prenom.trim() || 'votre enfant';
  const maj = (champs: Partial<Fiche>) => setFiche((f) => (f ? { ...f, ...champs } : f));
  const resume = resumeClasse(depart.identite.ecole, depart.identite.classeId, classesConnues, annee);

  const edition = (() => {
    if (!enModification) return null;
    const commun = {
      erreurs,
      onPrecedent: annuler,
      onSuivant: () => void enregistrer(enModification),
      prenom: fiche.identite.prenom,
    };
    let etape: React.ReactNode = null;
    switch (enModification) {
      case 'identite':
        etape = (
          <EtapeIdentite
            valeur={fiche.identite}
            onChange={(identite) => maj({ identite })}
            erreurs={erreurs}
            onSuivant={commun.onSuivant}
            photo={photo}
            onPhoto={setPhoto}
            photoExistante={ligne.photo_signed_url ?? null}
            /* Pas de copie depuis la fratrie : on corrige une fiche qui existe. */
            fratrie={[]}
            onCopierFratrie={() => {}}
            classes={classesConnues}
            annee={annee}
            onSignalerClasse={(classe) =>
              signalerClasseManquante({
                ecoleId: fiche.identite.ecole,
                classe,
                enfant: { prenom: fiche.identite.prenom.trim(), nom: fiche.identite.nom.trim() },
              })
            }
          />
        );
        break;
      case 'sante':
        etape = <EtapeSante {...commun} valeur={fiche.sante} onChange={(sante) => maj({ sante })} />;
        break;
      case 'vie':
        etape = <EtapeVie {...commun} valeur={fiche.vie} onChange={(vie) => maj({ vie })} />;
        break;
      case 'sorties':
        etape = <EtapeSorties {...commun} valeur={fiche.sorties} onChange={(sorties) => maj({ sorties })} />;
        break;
      case 'autorisations':
        etape = (
          <EtapeAutorisations
            {...commun}
            valeur={fiche.autorisations}
            onChange={(autorisations) => maj({ autorisations })}
          />
        );
        break;
    }
    return (
      <EnModification onAnnuler={annuler} enCours={enregistrement}>
        {echec && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
            <p className="text-sm text-red-800">{echec}</p>
          </div>
        )}
        {tonDesManques ? <TonDesManques>{etape}</TonDesManques> : etape}
      </EnModification>
    );
  })();

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Link to="/kids" className="mb-6 inline-flex min-h-[44px] items-center text-gray-600 hover:text-gray-900">
        <ArrowLeft className="mr-2 h-5 w-5" /> Mes enfants
      </Link>

      <div className="mb-6 flex items-center gap-4">
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-gray-100 sm:h-20 sm:w-20">
          {ligne.photo_signed_url ? (
            <img src={ligne.photo_signed_url} alt={`${ligne.prenom} ${ligne.nom}`} className="h-full w-full object-cover" />
          ) : (
            <User className="h-full w-full p-4 text-gray-300" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-gray-900 sm:text-3xl">
            {ligne.prenom} {ligne.nom}
          </h1>
          <p className="text-gray-600">
            {getAgeFromDate(ligne.date_naissance)} ans · né(e) le{' '}
            {new Date(ligne.date_naissance).toLocaleDateString('fr-BE')}
          </p>
          {/*
            La photo se changeait au survol de la vignette : invisible et
            inatteignable sur un téléphone. Elle se change dans « Votre
            enfant », comme à la création.
          */}
          {!enModification && (
            <button
              type="button"
              onClick={() => ouvrir('identite')}
              className="mt-1 inline-flex min-h-[36px] items-center text-sm font-medium text-primary-700 hover:text-primary-800"
            >
              <Camera className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {ligne.photo_signed_url ? 'Changer la photo' : 'Ajouter une photo'}
            </button>
          )}
        </div>
      </div>

      {!enModification && (
        <BandeauManques completude={completude} prenom={prenom} kidId={id} onAller={ouvrir} />
      )}

      <Recapitulatif
        /* Les blocs fermés montrent ce qui est enregistré ; l'étape ouverte, ce qu'on est en train de changer. */
        fiche={depart}
        photo={null}
        photoExistante={ligne.photo_signed_url ?? null}
        ecoles={schools}
        personnes={persons}
        onModifier={(e) => ouvrir(e as EtapeFiche)}
        classe={resume ? <LigneClasse resume={resume} /> : undefined}
        consultation={{
          manques: manquesParEtape(completude),
          enModification,
          edition,
        }}
      />

      {!enModification && <BarreVerifier completude={completude} kidId={id} />}

      <Toaster position="top-center" />
    </div>
  );
};

export default KidDetailsPage;
